import React, { useCallback, useContext, useEffect, useState, memo } from "react";
import * as Sentry from "@sentry/react";
import { observer } from "mobx-react";
import { RootStoreContext } from "src/stores/RootStore";
import { UI } from "@wwimmo/ui";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import styles from "./AgendaItemsSidebar.module.css";
import { useTranslation } from "react-i18next";
import { AgendaItem, AgendaItemChild } from "src/types";
import { ColorStyle } from "src/utils/Colors";
import { SCREEN_SIZE } from "src/stores/UIStore";
import { ASSEMBLY_STATE, LOADING_TYPE } from "src/stores/assemblies/AssemblyStore";

// Subcomponent: AddTooltipMenu
type AddTooltipMenuProps = {
    itemId: string;
    isParent: boolean;
    index: number;
    parentIndex?: number;
    handleAddNewItem: (isParentAction: boolean, parentIdx: number | null, childIdx: number | null) => () => void;
    t: (key: string) => string | undefined;
};

const AddTooltipMenu = memo<AddTooltipMenuProps>(({ itemId, isParent, index, parentIndex, handleAddNewItem, t }) => {
    const [open, setOpen] = useState<boolean>(false);

    // Compute parentIdx and childIdx once
    const parentIdx = isParent ? index : parentIndex!;
    const childIdx = isParent ? null : index;

    // Toggle the menu open/closed
    const onClickAddText = useCallback(() => {
        setOpen((prev) => !prev);
    }, []);

    // Add child item: calls handleAddNewItem(false, parentIdx, childIdx)
    const handleAddChildClick = useCallback(() => {
        handleAddNewItem(false, parentIdx, childIdx)();
        setOpen(false);
    }, [handleAddNewItem, parentIdx, childIdx]);

    // Add parent item: calls handleAddNewItem(true, parentIdx, null)
    const handleAddParentClick = useCallback(() => {
        handleAddNewItem(true, parentIdx, null)();
        setOpen(false);
    }, [handleAddNewItem, parentIdx]);

    // Prevent clicks inside this menu from bubbling up and selecting the row
    const handleStopPropagation = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
    }, []);

    return (
        <div className={styles.HoverText} onClick={handleStopPropagation}>
            <UI.Icon icon={UI.SVGIcon.Add} size={18} onClick={onClickAddText} />

            {open && (
                <div className={styles.AddTooltipMenu}>
                    <div className={styles.AddOption} onClick={handleAddChildClick}>
                        {t("screens.assemblies.agenda.agendaitemchild")}
                    </div>
                    <div className={styles.AddOption} onClick={handleAddParentClick}>
                        {t("screens.assemblies.agenda.agendaitem")}
                    </div>
                </div>
            )}
        </div>
    );
});

// Maincomponent: AgendaItemsSidebarBase
const AgendaItemsSidebarBase = observer(() => {
    const { t } = useTranslation();
    const { agendaitemStore, uiStore, assemblyStore, navStore } = useContext(RootStoreContext);

    useEffect(() => {
        if (assemblyStore.loadingType === LOADING_TYPE.ASSEMBLY) {
            agendaitemStore.loadAgendaItems();
        }
    }, [agendaitemStore.currentAgendaItems, assemblyStore.loadingType, agendaitemStore]);

    // When an item is clicked, select it in the store and focus it
    const onClickItem = useCallback(
        (itemId: string) => () => {
            assemblyStore.setIsFreetextHeaderExpanded(false);
            assemblyStore.setIsFreetextFooterExpanded(false);

            const allItems = agendaitemStore.currentAgendaItems;
            const foundItem =
                allItems.find((item) => item.id === itemId) ||
                allItems.flatMap((parent) => parent.agendaitemchild || []).find((child) => child.id === itemId);

            if (foundItem) {
                agendaitemStore.setCurrentAgendaItem(foundItem);
                assemblyStore.setFocusedAgendaItem(foundItem);
            } else {
                console.error(`Item with id ${itemId} not found`);
            }
        },
        [agendaitemStore, assemblyStore]
    );

    const handleDragEnd = useCallback(
        (result: any) => {
            const { source, destination } = result;
            if (!destination) return;
            agendaitemStore.handleDragEnd(source, destination);
        },
        [agendaitemStore]
    );

    // Returns a callback that adds either a parent or child
    const handleAddNewItem = useCallback(
        (isParentAction: boolean, parentIndex: number | null = null, childIndex: number | null = null) =>
            () => {
                agendaitemStore.handleAddNewItem(isParentAction, parentIndex, childIndex);
            },
        [agendaitemStore]
    );

    const onClickRemoveAgenda = useCallback(
        (itemId: string) => () => {
            agendaitemStore.removeItemById(itemId);
        },
        [agendaitemStore]
    );

    const onClickUnlockAgendaItems = useCallback(() => {
        agendaitemStore.setIsModalUnlockDisplayed(true);
    }, [agendaitemStore]);

    const onClickEditIcon = useCallback(() => {
        uiStore.setInvoicePaneWidth(agendaitemStore.isAgendaItemsSidebarEditing ? 350 : 470);
        agendaitemStore.setIsAgendaItemsSidebarEditing(!agendaitemStore.isAgendaItemsSidebarEditing);
    }, [agendaitemStore, uiStore]);

    const handleResetAgendaitemsClick = useCallback(() => {
        agendaitemStore.setIsResetAgendaItemsModalDisplayed(true);
    }, [agendaitemStore]);

    const onResetAllAgendaitems = useCallback(
        (isResetAgendaItems: boolean) => async () => {
            if (isResetAgendaItems) {
                await agendaitemStore.deleteAllAgendaItems();
            }

            agendaitemStore.setIsResetAgendaItemsModalDisplayed(false);
        },
        [agendaitemStore]
    );

    const onClickFreeText = useCallback(
        (isHeader: boolean) => () => {
            if (isHeader) {
                assemblyStore.setIsFreetextHeaderExpanded(true);
                assemblyStore.setIsFreetextFooterExpanded(false);
            } else {
                assemblyStore.setIsFreetextFooterExpanded(true);
                assemblyStore.setIsFreetextHeaderExpanded(false);
            }
        },
        [agendaitemStore]
    );

    // Render a parent or child item, with behavior depending on editingMode
    const AgendaItemRow = observer(
        ({
            item,
            index,
            type,
            parentIndex
        }: {
            item: AgendaItem | AgendaItemChild;
            index: number;
            type: "PARENT" | "CHILD";
            parentIndex?: number | null;
        }) => {
            const isParent = type === "PARENT";
            const isLocked = assemblyStore.isAssemblyReadOnly;
            const editingMode = agendaitemStore.isAgendaItemsSidebarEditing;
            const isSelected =
                assemblyStore.isFreetextFooterExpanded || assemblyStore.isFreetextHeaderExpanded
                    ? false
                    : item.id === agendaitemStore.currentAgendaItem?.id;
            const isDragDisabled = isLocked;

            return (
                <Draggable draggableId={item.id} index={index} key={item.id} isDragDisabled={isDragDisabled}>
                    {(provided) => (
                        <div
                            ref={provided.innerRef}
                            {...(!isDragDisabled ? provided.draggableProps : {})}
                            className={`${isParent ? styles.ParentItem : styles.ChildItem} ${
                                isSelected ? styles.SelectedItem : ""
                            }`}
                            onClick={onClickItem(item.id)}
                        >
                            <div
                                {...(!isDragDisabled ? provided.dragHandleProps : {})}
                                className={isDragDisabled ? styles.DisableDragHandle : styles.DragHandle}
                            >
                                {isLocked ? (
                                    <UI.Icon
                                        className={styles.IconLock}
                                        icon={UI.SVGIcon.Lock}
                                        size={"small"}
                                        onClick={onClickUnlockAgendaItems}
                                    />
                                ) : editingMode ? (
                                    <UI.Icon
                                        className={styles.IconFrame}
                                        icon={UI.SVGIcon.Frame}
                                        color={ColorStyle("light-gray")}
                                    />
                                ) : null}
                            </div>

                            <div className={isParent ? styles.ParentRow : styles.ChildRow}>
                                <div className={styles.ItemSort}>
                                    {isParent ? `${item.sort}` : `${parentIndex! + 1}.${item.sort}`}
                                </div>
                                <div className={isParent ? styles.ItemText : styles.ItemTextChild}>
                                    {item.text || ""}
                                </div>

                                {editingMode && uiStore.currentScreenSize > SCREEN_SIZE.LAPTOP && !isLocked && (
                                    <div className={styles.HoverContainer}>
                                        <AddTooltipMenu
                                            itemId={item.id}
                                            isParent={isParent}
                                            index={index}
                                            parentIndex={parentIndex!}
                                            handleAddNewItem={handleAddNewItem}
                                            t={t}
                                        />
                                        <UI.Icon
                                            icon={UI.SVGIcon.Close}
                                            className={styles.CloseIcon}
                                            size={20}
                                            onClick={onClickRemoveAgenda(item.id)}
                                        />
                                    </div>
                                )}
                                {item.agendaitemfiles && item.agendaitemfiles.length > 0 ? (
                                    <UI.Icon icon={UI.SVGIcon.Documents} className={styles.DocumentIcon} size={26} />
                                ) : null}
                                {item.hasVotings && navStore.hasFeatureFlagAssemblyVoting ? (
                                    <UI.Icon icon={UI.SVGIcon.Voting} className={styles.VotingIcon} size={28} />
                                ) : null}
                            </div>
                        </div>
                    )}
                </Draggable>
            );
        }
    );

    return (
        <UI.Container className={styles.AgendaItemContainer}>
            <div
                className={styles.FirstRowEdit}
                onClick={agendaitemStore.isAgendaItemsSidebarEditing ? undefined : onClickEditIcon}
            >
                {agendaitemStore.isAgendaItemsSidebarEditing ? (
                    <div className={styles.EditModeContainer}>
                        {assemblyStore.currentAssembly?.state === ASSEMBLY_STATE.PROTOCOL_PUBLISHED ||
                        assemblyStore.currentAssembly?.state === ASSEMBLY_STATE.CLOSED ? null : (
                            <div className={styles.ResetAgendaItemDiv} onClick={handleResetAgendaitemsClick}>
                                {t("screens.assemblies.agenda.reset_agendaitems")}
                            </div>
                        )}
                        <div className={styles.EndEditDiv} onClick={onClickEditIcon}>
                            {t("screens.assemblies.vote.end_editing")}
                        </div>
                        <UI.Modal
                            show={agendaitemStore.isResetAgendaItemsModalDisplayed}
                            title={t("screens.assemblies.agenda.reset_agendaitems").toString()}
                        >
                            <div>{t("screens.assemblies.agenda.confirm_reset_agendaitems")}</div>
                            <div className={styles.ButtonContainerModal}>
                                <UI.Button
                                    label={t("labels.yes")}
                                    onClick={onResetAllAgendaitems(true)}
                                    className={styles.ButtonModalYes}
                                />
                                <UI.Button
                                    label={t("labels.no")}
                                    className={styles.ButtonModalNo}
                                    onClick={onResetAllAgendaitems(false)}
                                />
                            </div>
                        </UI.Modal>
                    </div>
                ) : (
                    <UI.Icon icon={UI.SVGIcon.Edit} color={ColorStyle("primary")} size={28} />
                )}
            </div>

            <div
                className={assemblyStore.isFreetextHeaderExpanded ? styles.SelectedFreeTextItem : styles.FixedItem}
                onClick={onClickFreeText(true)}
            >
                <div className={styles.ParentRow}>
                    <div className={`ml-2 ${styles.ItemText}`}>{t("screens.assemblies.agenda.introduction")}</div>
                </div>
            </div>

            <DragDropContext
                onDragEnd={
                    agendaitemStore.isAgendaItemsSidebarEditing && !assemblyStore.isAssemblyReadOnly
                        ? handleDragEnd
                        : undefined
                }
            >
                <Droppable droppableId="agenda-list" type="PARENT">
                    {(provided) => (
                        <div ref={provided.innerRef} {...provided.droppableProps}>
                            {agendaitemStore.currentAgendaItems.map((parentItem, parentIndex) => (
                                <React.Fragment key={parentItem.id}>
                                    <AgendaItemRow item={parentItem} index={parentIndex} type="PARENT" />

                                    <Droppable droppableId={`children-${parentItem.id}`} type="CHILD">
                                        {(provided) => (
                                            <div ref={provided.innerRef} {...provided.droppableProps}>
                                                {parentItem.agendaitemchild?.map((childItem, childIndex) => (
                                                    <AgendaItemRow
                                                        key={childItem.id}
                                                        item={childItem}
                                                        index={childIndex}
                                                        type="CHILD"
                                                        parentIndex={parentIndex}
                                                    />
                                                ))}
                                                {provided.placeholder}
                                            </div>
                                        )}
                                    </Droppable>
                                </React.Fragment>
                            ))}
                            {provided.placeholder}
                        </div>
                    )}
                </Droppable>
            </DragDropContext>

            <div
                className={assemblyStore.isFreetextFooterExpanded ? styles.SelectedFreeTextItem : styles.FixedItem}
                onClick={onClickFreeText(false)}
            >
                <div className={styles.ParentRow}>
                    <div className={`ml-2 ${styles.ItemText}`}>{t("screens.assemblies.agenda.conclusion")}</div>
                </div>
            </div>
        </UI.Container>
    );
});

export const AgendaItemsSidebar = Sentry.withProfiler(AgendaItemsSidebarBase);
